import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ApiError, fetchPublicResearch, type PublicResearchItem } from '../api/client';
import { POSTER_SANDBOX, usePosterColorScheme, withPosterLinkTargets } from '../lib/poster';

/** Fetch state for the public full-screen poster route. */
type PublicPosterState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; item: PublicResearchItem };

/**
 * Unauthenticated full-screen poster for visibility='public' research, served
 * at /p/:slug/poster outside RequireAuth. Renders only the sandboxed poster in
 * a `100dvh` iframe that scrolls natively, with one pill linking to the public
 * share page (/p/:slug?details=1) for the text version.
 */
export function PublicPosterPage() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<PublicPosterState>({ status: 'loading' });
  const posterColorScheme = usePosterColorScheme();

  useEffect(() => {
    setState({ status: 'loading' });
    fetchPublicResearch(slug)
      .then((item) => setState({ status: 'loaded', item }))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) {
          setState({ status: 'error', message: 'This research is not available.' });
          return;
        }
        setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
      });
  }, [slug]);

  if (state.status === 'loading') {
    return (
      <div className="fixed inset-0 grid place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="fixed inset-0 grid place-items-center px-4 text-center text-sm text-muted-foreground">
        {state.message}
      </div>
    );
  }

  // Nothing to show full-screen without poster HTML; the share page renders the markdown.
  if (!state.item.body_html) {
    return <Navigate to={`/p/${slug}?details=1`} replace />;
  }

  return (
    <div className="fixed inset-0">
      {/*
        SECURITY (load-bearing): agent-authored poster in a sandboxed iframe
        WITHOUT allow-same-origin. NEVER add allow-same-origin.
        (See web/src/lib/poster.ts.)
      */}
      <iframe
        title={state.item.title}
        srcDoc={withPosterLinkTargets(state.item.body_html)}
        sandbox={POSTER_SANDBOX}
        className="block border-0"
        style={{ height: '100dvh', width: '100%', colorScheme: posterColorScheme }}
      />
      <Link
        to={`/p/${slug}?details=1`}
        className="fixed left-4 top-4 z-10 rounded-full border border-border bg-background/80 px-3 py-1.5 text-sm backdrop-blur hover:bg-background"
      >
        ← {state.item.title}
      </Link>
    </div>
  );
}
